import {useEffect, useMemo, useState} from 'react';
import {useLocation, useMatch} from 'react-router-dom';

import {enhanceRegionUuid} from '@modules/piu/features/productSheet/helpers/productSheetHelper';
import {selectCountry, selectGmc, selecIsLoadingResolveContext, selectProduct, selectRegion, selectRmc} from '@modules/piu/features/productSheet/store/productSheetSelectors';
import Breadcrumb from '@common/components/Breadcrumb';
import SkeletonLine from '@common/components/Skeleton/SkeletonLine';
import useProductSheetStore from '@modules/piu/features/productSheet/store/productSheetStore';

type CrumbId = 'product' | 'gmc' | 'rmc' | 'country' | 'translations';

interface Crumb {
  id: CrumbId;
  name: string;
  tooltip?: string;
  action?: () => void;
  link?: string;
}

const ProductBreadcrumbs = () => {
  // Match translations page
  const match = useMatch('/product-sheet/:productId/translations');
  const {state} = useLocation();
  const showTranslationCrumb = !!match;
  const product = useProductSheetStore(selectProduct);
  const gmc = useProductSheetStore(selectGmc);
  const rmc = useProductSheetStore(selectRmc);
  const region = useProductSheetStore(selectRegion);
  const country = useProductSheetStore(selectCountry);
  const {changeGmc, changeProduct, changeRmc} = useProductSheetStore();
  const isLoading = useProductSheetStore(selecIsLoadingResolveContext);

  const [breadcrumbs, setBreadcrumbs] = useState<Crumb[]>([]);

  const MAX_CRUMBS_TO_DISPLAY = 3;

  const buildPath = (id: Omit<CrumbId, 'translations'>) => {
    const basePath = `/product-sheet/${state?.mainItem ?? product?.mainItem}`;
    switch (id) {
      case 'product':
        return basePath;
      case 'gmc':
        return `${basePath}?version=${gmc?.id}`;
      case 'rmc':
        return `${basePath}?version=${gmc?.id}&region=${region?.id}`;
      case 'country':
        return `${basePath}?version=${gmc?.id}&region=${region?.id}&country=${country?.id}`;
      default:
        return '';
    }
  };

  const buildBreadcrumbs = useMemo<Crumb[]>(() => {
    // If there is no product, there are no breadcrumbs
    if (!product && !state) return [];

    // Portfolio is always the first crumb
    const crumbs: Crumb[] = [];

    // Add the rest of the crumbs
    if (product) {
      const crumb: Crumb = {
        id: 'product',
        name: product.name!,
        tooltip: 'Return to Product',
        action: () => changeProduct(product),
        link: buildPath('product')
      };
      crumbs.push(crumb);
    }

    if (gmc) {
      const crumb: Crumb = {
        id: 'gmc',
        name: gmc.name,
        tooltip: 'Return to Global Marketing Content',
        action: () => changeGmc(gmc),
        link: buildPath('gmc')
      };
      crumbs.push(crumb);
    }

    if (region && rmc && gmc) {
      const crumb: Crumb = {
        id: 'rmc',
        name: region.name,
        tooltip: 'Return to Regional Marketing Content',
        action: () =>
          changeRmc({
            gmc,
            rmc,
            country: null,
            region: enhanceRegionUuid(region.id)
          }),
        link: buildPath('rmc')
      };
      crumbs.push(crumb);
    }

    if (rmc && country) {
      const crumb: Crumb = {
        id: 'country',
        name: country.name,
        tooltip: 'Return to Country Override'
      };
      crumbs.push(crumb);
    }

    if (showTranslationCrumb) {
      const crumb: Crumb = {
        id: 'translations',
        name: 'Translations',
        tooltip: 'Return to Translations'
      };
      crumbs.push(crumb);
    }

    return crumbs;
  }, [product, gmc, rmc, country, region, showTranslationCrumb]);

  useEffect(() => {
    setBreadcrumbs(buildBreadcrumbs);
  }, [buildBreadcrumbs]);

  if (!product) return null;

  return isLoading ? <SkeletonLine height={5} /> : <Breadcrumb crumbs={breadcrumbs} maxCrumbsToDisplay={MAX_CRUMBS_TO_DISPLAY} />;
};

export default ProductBreadcrumbs;
